import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, Upload, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import logo from '../../assets/images/logo-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const GOALS = [
  { id: "reach", label: "📊 Understand my reach and audience value" },
  { id: "brands", label: "🎯 Find brand partnership opportunities" },
  { id: "pricing", label: "💰 Price my content for sponsorships" },
  { id: "production", label: "🎬 Get production-ready for my next project" },
  { id: "growth", label: "📈 Track my growth over time" },
  { id: "pitch", label: "🤝 Pitch to investors or studios" },
];


export default function OnboardingStep3() {
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleGoal = (goalId: string) => {
    setSelectedGoals(prev =>
      prev.includes(goalId)
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };


  const handleContinue = async () => {
    if (selectedGoals.length === 0) {
      toast.error("Please select at least one goal");
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.updateCurrentUser({
        goals: selectedGoals,
        onboarding_step: 3,
      });

      await checkAuth();
      navigate("/onboarding/step4");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update preferences");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center">
      {/* Background Grid */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[oklch(1.0000_0_0)] to-transparent"></div>
      </div>

      {/* Top Navigation */}
      <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-50 relative">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
        >
          <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
        </button>
        <div className="flex items-center gap-4">
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
          </div>
          <span className="text-xs font-medium text-[oklch(0.55_0_0)]">Step 2 of 3</span>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
        {/* Header */}
        <div className="animate-in slide-in-from-bottom-4 duration-500 flex flex-col mb-8 relative items-center">
          <div className="relative w-20 h-20 mb-4">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] rounded-full blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-full h-full rounded-full object-cover border-4 border-[oklch(1.0000_0_0)] shadow-xl relative z-10 animate-[float_3s_ease-in-out_infinite]"
            />
            <div className="-right-28 text-[oklch(1.0000_0_0)] transform animate-in slide-in-from-bottom-2 duration-500 delay-300 text-xs font-medium bg-orange-500 z-20 rounded-xl px-3 py-1.5 absolute top-0 shadow-lg rotate-2">
              Define your goals 🎯
            </div>
          </div>
        </div>

        <div className="text-center mb-12 animate-in slide-in-from-bottom-4 duration-500">
          <h1 className="text-3xl md:text-4xl font-semibold font-cabinet tracking-tight text-[oklch(0.2686_0_0)] mb-3">
            What brings you to Artie?
          </h1>
          <p className="text-[oklch(0.55_0_0)] font-ibm text-sm md:text-base max-w-md mx-auto leading-relaxed">
            Select your goals so we can personalize your experience and recommendations.
          </p>
        </div>

        {/* Goals Section */}
        <div className="w-full mb-12 animate-in slide-in-from-bottom-4 duration-500 delay-100">
          <Card className="p-6 bg-white border border-[oklch(0.92_0_0)] rounded-2xl shadow-sm">
            <h3 className="text-lg font-semibold font-cabinet text-[oklch(0.2686_0_0)] mb-4">
              Select your goals (choose all that apply)
            </h3>
            <div className="space-y-2">
              {GOALS.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => toggleGoal(goal.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                    selectedGoals.includes(goal.id)
                      ? "border-[oklch(0.70_0.18_55)] bg-[oklch(0.70_0.18_55)]/5"
                      : "border-[oklch(0.92_0_0)] hover:border-[oklch(0.70_0.18_55)]/50"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedGoals.includes(goal.id)
                      ? "border-[oklch(0.70_0.18_55)] bg-[oklch(0.70_0.18_55)]"
                      : "border-[oklch(0.92_0_0)]"
                  }`}>
                    {selectedGoals.includes(goal.id) && (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-[oklch(0.2686_0_0)]">{goal.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Continue Button */}
        <div className="w-full max-w-2xl flex items-center justify-end animate-in slide-in-from-bottom-4 duration-500 delay-200">
          <Button
            onClick={handleContinue}
            disabled={isSubmitting || selectedGoals.length === 0}
            className="group flex items-center gap-2 bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-[oklch(1.0000_0_0)] px-8 py-3 rounded-xl text-sm font-medium transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 font-ibm disabled:opacity-70 disabled:hover:translate-y-0"
          >
            <span>{isSubmitting ? "Saving..." : "Continue"}</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </main>
    </div>
  );
}